import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ThemeService } from 'src/app/services/theme.service';

@Component({
  selector: 'app-json-metadata-viewer',
  templateUrl: './json-metadata-viewer.component.html',
  styleUrls: ['./json-metadata-viewer.component.scss']
})
export class JsonMetadataViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: any;
  @Input() label: string = 'JSON Data';

  @ViewChild('jsonContainer', { static: true }) jsonContainer!: ElementRef;

  private themeSubscription: Subscription;
  private isDarkTheme = false;

  constructor(private themeService: ThemeService) {}

  ngOnInit(): void {
    this.themeSubscription = this.themeService.isDark$.subscribe(isDark => {
      this.isDarkTheme = isDark;
      if (this.jsonContainer?.nativeElement) {
        this.jsonContainer.nativeElement.classList.toggle('theme-dark', isDark);
        this.jsonContainer.nativeElement.classList.toggle('theme-light', !isDark);
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.data) {
      setTimeout(() => {
        this.initializeJsonViewer();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe();
    if (this.jsonContainer?.nativeElement) {
      this.jsonContainer.nativeElement.innerHTML = '';
    }
  }

  private async initializeJsonViewer(): Promise<void> {
    if (!this.jsonContainer?.nativeElement || !this.data) {
      return;
    }

    try {
      // Clear any existing content
      this.jsonContainer.nativeElement.innerHTML = '';
      
      // Detect current theme
      const isDarkTheme = this.isDarkTheme;
      
      // Add theme class to container
      this.jsonContainer.nativeElement.classList.add(
        isDarkTheme ? 'theme-dark' : 'theme-light'
      );

      // Create interactive JSON viewer
      const jsonContainer = document.createElement('div');
      jsonContainer.className = 'json-content';
      jsonContainer.appendChild(this.renderJsonValue(this.data, '', 0));
      
      this.jsonContainer.nativeElement.appendChild(jsonContainer);

      // Add custom toolbar for better UX
      this.addCustomToolbar();

    } catch (error) {
      console.error('Error initializing JSON viewer:', error);
      this.showErrorFallback();
    }
  }

  private renderJsonValue(value: any, key: string = '', depth: number = 0): HTMLElement {
    const element = document.createElement('div');
    element.className = 'json-item';
    element.style.marginLeft = `${depth * 20}px`;

    if (value === null) {
      element.innerHTML = `${key ? `<span class="json-key">"${key}"</span>: ` : ''}<span class="json-null">null</span>`;
    } else if (typeof value === 'boolean') {
      element.innerHTML = `${key ? `<span class="json-key">"${key}"</span>: ` : ''}<span class="json-boolean">${value}</span>`;
    } else if (typeof value === 'number') {
      element.innerHTML = `${key ? `<span class="json-key">"${key}"</span>: ` : ''}<span class="json-number">${value}</span>`;
    } else if (typeof value === 'string') {
      element.innerHTML = `${key ? `<span class="json-key">"${key}"</span>: ` : ''}<span class="json-string">"${this.escapeHtml(value)}"</span>`;
    } else if (Array.isArray(value)) {
      return this.renderCollapsibleArray(value, key, depth);
    } else if (typeof value === 'object') {
      return this.renderCollapsibleObject(value, key, depth);
    }

    return element;
  }

  private renderCollapsibleObject(obj: any, key: string, depth: number): HTMLElement {
    const container = document.createElement('div');
    container.className = 'json-collapsible';

    const keys = Object.keys(obj);
    const isEmpty = keys.length === 0;

    // Header with toggle
    const header = document.createElement('div');
    header.className = 'json-header';
    header.style.marginLeft = `${depth * 20}px`;
    
    const toggle = document.createElement('span');
    toggle.className = 'json-toggle';
    toggle.textContent = isEmpty ? '' : '▶';  // Start collapsed
    toggle.style.cursor = isEmpty ? 'default' : 'pointer';
    
    const keySpan = key ? `<span class="json-key">"${key}"</span>: ` : '';
    const objectInfo = `<span class="json-bracket">{</span><span class="json-size-info">${keys.length} ${keys.length === 1 ? 'property' : 'properties'}</span><span class="json-bracket">}</span>`;
    
    header.innerHTML = `${toggle.outerHTML}${keySpan}${objectInfo}`;
    container.appendChild(header);

    // Content
    if (!isEmpty) {
      const content = document.createElement('div');
      content.className = 'json-content-collapsible';
      content.style.display = 'none';  // Start collapsed
      
      keys.forEach((objKey, index) => {
        content.appendChild(this.renderJsonValue(obj[objKey], objKey, depth + 1));
        if (index < keys.length - 1) {
          const comma = document.createElement('span');
          comma.className = 'json-comma';
          comma.textContent = ',';
          content.appendChild(comma);
        }
      });
      
      container.appendChild(content);

      // Add click event for toggle
      header.addEventListener('click', () => {
        const isCollapsed = content.style.display === 'none';
        content.style.display = isCollapsed ? 'block' : 'none';
        toggle.textContent = isCollapsed ? '▼' : '▶';
      });
    }

    return container;
  }

  private renderCollapsibleArray(arr: any[], key: string, depth: number): HTMLElement {
    const container = document.createElement('div');
    container.className = 'json-collapsible';

    const isEmpty = arr.length === 0;

    // Header with toggle
    const header = document.createElement('div');
    header.className = 'json-header';
    header.style.marginLeft = `${depth * 20}px`;
    
    const toggle = document.createElement('span');
    toggle.className = 'json-toggle';
    toggle.textContent = isEmpty ? '' : '▶';  // Start collapsed
    toggle.style.cursor = isEmpty ? 'default' : 'pointer';
    
    const keySpan = key ? `<span class="json-key">"${key}"</span>: ` : '';
    const arrayInfo = `<span class="json-bracket">[</span><span class="json-size-info">${arr.length} ${arr.length === 1 ? 'item' : 'items'}</span><span class="json-bracket">]</span>`;
    
    header.innerHTML = `${toggle.outerHTML}${keySpan}${arrayInfo}`;
    container.appendChild(header);

    // Content
    if (!isEmpty) {
      const content = document.createElement('div');
      content.className = 'json-content-collapsible';
      content.style.display = 'none';  // Start collapsed
      
      arr.forEach((item, index) => {
        content.appendChild(this.renderJsonValue(item, `${index}`, depth + 1));
        if (index < arr.length - 1) {
          const comma = document.createElement('span');
          comma.className = 'json-comma';
          comma.textContent = ',';
          content.appendChild(comma);
        }
      });
      
      container.appendChild(content);

      // Add click event for toggle
      header.addEventListener('click', () => {
        const isCollapsed = content.style.display === 'none';
        content.style.display = isCollapsed ? 'block' : 'none';
        toggle.textContent = isCollapsed ? '▼' : '▶';
      });
    }

    return container;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private addCustomToolbar(): void {
    if (!this.jsonContainer?.nativeElement) return;

    const toolbar = document.createElement('div');
    toolbar.className = 'json-custom-toolbar';
    toolbar.innerHTML = `
      <div class="toolbar-left">
        <span class="json-info">
          ${this.getDataInfo()}
        </span>
      </div>
      <div class="toolbar-right">
        <button class="json-action-btn" data-action="expand">
           Expand All
        </button>
        <button class="json-action-btn" data-action="collapse">
          Collapse All
        </button>
        <button class="json-action-btn" data-action="copy">
          Copy JSON
        </button>
      </div>
    `;

    // Insert toolbar at the beginning
    this.jsonContainer.nativeElement.insertBefore(
      toolbar, 
      this.jsonContainer.nativeElement.firstChild
    );

    // Add event listeners for toolbar buttons
    this.setupToolbarEvents(toolbar);
  }

  private getDataInfo(): string {
    if (!this.data) return 'No data';
    
    if (Array.isArray(this.data)) {
      return `Array (${this.data.length} items)`;
    } else if (typeof this.data === 'object') {
      const keys = Object.keys(this.data);
      return `Object (${keys.length} properties)`;
    } else {
      return `${typeof this.data}`;
    }
  }

  private setupToolbarEvents(toolbar: HTMLElement): void {
    toolbar.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const action = target.getAttribute('data-action');
      
      if (!action) return;
      
      switch (action) {
        case 'expand':
          this.expandAll();
          break;
        case 'collapse':
          this.collapseAll();
          break;
        case 'copy':
          this.copyToClipboard();
          break;
      }
    });
  }

  private expandAll(): void {
    const headers = this.jsonContainer.nativeElement.querySelectorAll('.json-header');
    headers.forEach((header: HTMLElement) => {
      const toggle = header.querySelector('.json-toggle') as HTMLElement;
      const content = header.parentElement?.querySelector('.json-content-collapsible') as HTMLElement;
      
      if (toggle && content && toggle.textContent === '▶') {
        content.style.display = 'block';
        toggle.textContent = '▼';
      }
    });
  }

  private collapseAll(): void {
    const headers = this.jsonContainer.nativeElement.querySelectorAll('.json-header');
    headers.forEach((header: HTMLElement) => {
      const toggle = header.querySelector('.json-toggle') as HTMLElement;
      const content = header.parentElement?.querySelector('.json-content-collapsible') as HTMLElement;
      
      if (toggle && content && toggle.textContent === '▼') {
        content.style.display = 'none';
        toggle.textContent = '▶';
      }
    });
  }

  private async copyToClipboard(): Promise<void> {
    try {
      const jsonString = JSON.stringify(this.data, null, 2);
      await navigator.clipboard.writeText(jsonString);
      
      // Show feedback
      this.showCopyFeedback();
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      this.fallbackCopyToClipboard();
    }
  }

  private showCopyFeedback(): void {
    const feedback = document.createElement('div');
    feedback.className = 'copy-feedback';
    feedback.textContent = 'Copied to clipboard';
    
    this.jsonContainer.nativeElement.appendChild(feedback);
    
    setTimeout(() => {
      if (feedback.parentNode) {
        feedback.parentNode.removeChild(feedback);
      }
    }, 2000);
  }

  private fallbackCopyToClipboard(): void {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = JSON.stringify(this.data, null, 2);
    document.body.appendChild(textArea);
    textArea.select();
    
    try {
      document.execCommand('copy');
      this.showCopyFeedback();
    } catch (error) {
      console.error('Fallback copy failed:', error);
    }
    
    document.body.removeChild(textArea);
  }

  private showErrorFallback(): void {
    if (!this.jsonContainer?.nativeElement) return;
    
    this.jsonContainer.nativeElement.innerHTML = `
      <div class="json-error-fallback">
        <h4>❌ Error displaying JSON</h4>
        <p>Unable to render the JSON viewer. Showing raw data:</p>
        <pre class="json-raw">${JSON.stringify(this.data, null, 2)}</pre>
      </div>
    `;
  }
} 